import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import api from '../../services/api';
import './CompactSKITimeline.css';

const MILESTONE_DEFS = [
  { key: 'plan',      label: 'Planning Starts', field: 'milestone_plan_date' },
  { key: 'dev',       label: 'Dev Starts',      field: 'milestone_dev_date' },
  { key: 'gpr',       label: 'GPR Flag',        field: 'milestone_gpr_date' },
  { key: 'gtm',       label: 'GTM Flag',        field: 'milestone_gtm_date' },
  { key: 'gtm_gprs',  label: 'GTM (GPRs)',      field: 'milestone_gtm_gprs_date' },
  { key: 'cuc',       label: 'Capability Update Catalog Verified', field: 'milestone_cuc_date' },
  { key: 'privacy',   label: 'Privacy',         field: 'milestone_privacy_date' },
  { key: 'ppp',       label: 'PPP',             field: 'milestone_ppp_date' },
  { key: 'codf',      label: 'Code Freeze',     field: 'milestone_codf_date' },
  { key: 'pa',        label: 'PA',              field: 'milestone_pa_date' },
  { key: 'ga',        label: 'GA',              field: 'milestone_ga_date' },
  { key: 'salesops',  label: 'SalesOps',        field: 'milestone_salesops_date' },
];

function buildMilestones(release) {
  return MILESTONE_DEFS
    .map(def => ({ key: def.key, label: def.label, date: release[def.field] }))
    .filter(m => m.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

function filterVisible(milestones) {
  // Show all milestones so the full release timeline is always visible
  return milestones;
}

const CompactSKITimeline = forwardRef((props, ref) => {
  const [releases, setReleases] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReleases();
    const handleRefresh = () => fetchReleases();
    window.addEventListener('refreshTimeline', handleRefresh);
    return () => window.removeEventListener('refreshTimeline', handleRefresh);
  }, []);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const response = await api.getSkiReleases();
      const all = response.data.data.skiReleases || [];

      // Keep active and planned releases that have at least one milestone date
      const withDates = all
        .filter(r =>
          (r.status === 'active' || r.status === 'planned') &&
          MILESTONE_DEFS.some(def => r[def.field])
        )
        .sort((a, b) => a.release_version.localeCompare(b.release_version));

      if (withDates.length === 0) {
        setReleases([]);
        return;
      }

      setReleases(withDates);

      // Default to the first active release, or the first planned, or index 0
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const activeIdx = withDates.findIndex(r => r.status === 'active');
      const plannedIdx = withDates.findIndex(r => r.status === 'planned');
      const defaultIdx = activeIdx !== -1 ? activeIdx : plannedIdx !== -1 ? plannedIdx : 0;
      setSelectedIndex(defaultIdx);
    } catch (err) {
      console.error('Error fetching releases for timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({ refresh: fetchReleases }));

  const handlePrev = () => setSelectedIndex(i => Math.max(0, i - 1));
  const handleNext = () => setSelectedIndex(i => Math.min(releases.length - 1, i + 1));

  const calculateCurrentPosition = (milestones) => {
    if (!milestones || milestones.length === 0) return '0%';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstDate = new Date(milestones[0].date);
    const lastDate  = new Date(milestones[milestones.length - 1].date);
    if (today < firstDate) return '0%';
    if (today > lastDate)  return '100%';
    const pct = ((today - firstDate) / (lastDate - firstDate)) * 100;
    return `${Math.max(0, Math.min(100, pct))}%`;
  };

  const calculateMilestonePosition = (milestoneDate, milestones) => {
    if (!milestones || milestones.length === 0) return '0%';
    const firstDate = new Date(milestones[0].date);
    const lastDate  = new Date(milestones[milestones.length - 1].date);
    const total = lastDate - firstDate;
    if (total === 0) return '0%';
    const pct = ((new Date(milestoneDate) - firstDate) / total) * 100;
    return `${Math.max(0, Math.min(100, pct))}%`;
  };

  const isPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(date) < today;
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  if (loading || releases.length === 0) return null;

  const release = releases[selectedIndex];
  const allMilestones = buildMilestones(release);
  const milestones = filterVisible(allMilestones);
  const currentPosition = calculateCurrentPosition(milestones);
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex < releases.length - 1;

  return (
    <div className="compact-ski-timeline">
      <button
        className={`compact-ski-timeline__cycle-btn ${!hasPrev ? 'compact-ski-timeline__cycle-btn--disabled' : ''}`}
        onClick={handlePrev}
        disabled={!hasPrev}
        title={hasPrev ? releases[selectedIndex - 1].full_name : 'No earlier release'}
        aria-label="Previous release"
      >
        ‹
      </button>

      <div className="compact-ski-timeline__label" title={`Status: ${release.status}`}>
        {release.full_name}
      </div>

      <div className="compact-ski-timeline__track">
        <div className="compact-ski-timeline__line" />
        {Object.values(
          milestones.reduce((groups, m) => {
            if (!groups[m.date]) groups[m.date] = [];
            groups[m.date].push(m);
            return groups;
          }, {})
        ).map((group) => {
          const date = group[0].date;
          const hasPa = group.some(m => m.key === 'pa');
          const hasGa = group.some(m => m.key === 'ga');
          const showLabel = hasPa ? 'PA' : hasGa ? 'GA' : null;
          const tooltipText = group.map(m => `${m.label} — ${formatDate(m.date)}`).join('\n');
          return (
            <div
              key={date}
              className={`compact-ski-timeline__milestone ${isPast(date) ? 'past' : 'future'}`}
              style={{
                left: calculateMilestonePosition(date, milestones),
                top: '50%',
              }}
              title={tooltipText}
            >
              <div className="compact-ski-timeline__marker" />
              {showLabel && (
                <span className="compact-ski-timeline__node-label" style={{ top: '8px' }}>
                  {showLabel}
                </span>
              )}
            </div>
          );
        })}
        <div
          className="compact-ski-timeline__current"
          style={{ left: currentPosition }}
          title={`Today\n${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
        />
      </div>

      <button
        className={`compact-ski-timeline__cycle-btn ${!hasNext ? 'compact-ski-timeline__cycle-btn--disabled' : ''}`}
        onClick={handleNext}
        disabled={!hasNext}
        title={hasNext ? releases[selectedIndex + 1].full_name : 'No later release'}
        aria-label="Next release"
      >
        ›
      </button>
    </div>
  );
});

CompactSKITimeline.displayName = 'CompactSKITimeline';

export default CompactSKITimeline;
